import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { isSafeUrl } from '@atlaskit/adf-schema';
import { ACTION, ACTION_SUBJECT, buildOpenedSettingsPayload, buildVisitedLinkPayload, EVENT_TYPE, INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { buildLayoutButtons, commandWithMetadata } from '@atlaskit/editor-common/card';
import { getLinkPreferencesURLFromENV } from '@atlaskit/editor-common/link';
import commonMessages, { linkMessages, linkToolbarMessages, cardMessages as messages } from '@atlaskit/editor-common/messages';
import { FLOATING_TOOLBAR_LINKPICKER_CLASSNAME, richMediaClassName } from '@atlaskit/editor-common/styles';
import { SmallerEditIcon } from '@atlaskit/editor-common/ui';
import { canRenderDatasource } from '@atlaskit/editor-common/utils';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos, removeSelectedNode } from '@atlaskit/editor-prosemirror/utils';
import RemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import CogIcon from '@atlaskit/icon/glyph/editor/settings';
import UnlinkIcon from '@atlaskit/icon/glyph/editor/unlink';
import OpenIcon from '@atlaskit/icon/glyph/shortcut';
import { fg } from '@atlaskit/platform-feature-flags';
import { changeSelectedCardToText } from './pm-plugins/doc';
import { pluginKey } from './pm-plugins/main';
import { DatasourceAppearanceButton } from './ui/DatasourceAppearanceButton';
import { editDatasource, EditDatasourceButton } from './ui/EditDatasourceButton';
import { buildEditLinkToolbar, editLink, editLinkToolbarConfig } from './ui/EditLinkToolbar';
import { EditToolbarButton } from './ui/EditToolbarButton';
import { HyperlinkToolbarAppearance } from './ui/HyperlinkToolbarAppearance';
import { LinkToolbarAppearance } from './ui/LinkToolbarAppearance';
import { ToolbarViewedEvent } from './ui/ToolbarViewedEvent';
import { appearanceForNodeType, displayInfoForCard, findCardInfo, isDatasourceConfigEditable, isDatasourceNode, titleUrlPairFromNode } from './utils';
export var removeCard = function removeCard(editorAnalyticsApi) {
  return commandWithMetadata(function (state, dispatch) {
    if (!(state.selection instanceof NodeSelection)) {
      return false;
    }
    var type = state.selection.node.type.name;
    var payload = {
      action: ACTION.DELETED,
      actionSubject: ACTION_SUBJECT.SMART_LINK,
      actionSubjectId: type,
      attributes: {
        inputMethod: INPUT_METHOD.TOOLBAR,
        displayMode: type
      },
      eventType: EVENT_TYPE.TRACK
    };
    if (dispatch) {
      var tr = state.tr;
      removeSelectedNode(tr);
      editorAnalyticsApi === null || editorAnalyticsApi === void 0 || editorAnalyticsApi.attachAnalyticsEvent(payload)(tr);
      dispatch(tr);
    }
    return true;
  }, {
    action: ACTION.DELETED
  });
};
export var visitCardLink = function visitCardLink(editorAnalyticsApi) {
  return function (state, dispatch) {
    if (!(state.selection instanceof NodeSelection)) {
      return false;
    }
    var type = state.selection.node.type;
    var _titleUrlPairFromNode = titleUrlPairFromNode(state.selection.node),
      url = _titleUrlPairFromNode.url;

    // All card links should open in the same tab per https://product-fabric.atlassian.net/browse/MS-1583.
    // We are in edit mode here, open the smart card URL in a new window.
    window.open(url);
    if (dispatch) {
      var tr = state.tr;
      editorAnalyticsApi === null || editorAnalyticsApi === void 0 || editorAnalyticsApi.attachAnalyticsEvent(buildVisitedLinkPayload(type.name))(tr);
      dispatch(tr);
    }
    return true;
  };
};
export var openLinkSettings = function openLinkSettings(editorAnalyticsApi, userPreferencesLink) {
  return function (state, dispatch) {
    if (!(state.selection instanceof NodeSelection)) {
      return false;
    }
    window.open(userPreferencesLink || getLinkPreferencesURLFromENV());
    if (dispatch) {
      var tr = state.tr,
        type = state.selection.node.type;
      editorAnalyticsApi === null || editorAnalyticsApi === void 0 || editorAnalyticsApi.attachAnalyticsEvent(buildOpenedSettingsPayload(type.name))(tr);
      dispatch(tr);
    }
    return true;
  };
};
export var floatingToolbar = function floatingToolbar(cardOptions, lpLinkPicker, platform, linkPickerOptions, pluginInjectionApi, disableFloatingToolbar) {
  return function (state, intl, providerFactory) {
    if (disableFloatingToolbar) {
      return;
    }
    var _state$schema$nodes = state.schema.nodes,
      inlineCard = _state$schema$nodes.inlineCard,
      blockCard = _state$schema$nodes.blockCard,
      embedCard = _state$schema$nodes.embedCard;
    var nodeType = [inlineCard, blockCard, embedCard];
    var pluginState = pluginKey.getState(state);
    if (!(state.selection instanceof NodeSelection)) {
      return;
    }
    var selectedNode = state.selection.node;
    if (!selectedNode) {
      return;
    }
    var isEmbedCard = appearanceForNodeType(selectedNode.type) === 'embed';

    /* add an offset to embeds due to extra padding */
    var toolbarOffset = isEmbedCard ? {
      offset: [0, 24]
    } : {};

    // Applies padding override for when link picker is currently displayed
    var className = pluginState !== null && pluginState !== void 0 && pluginState.showLinkingToolbar ? FLOATING_TOOLBAR_LINKPICKER_CLASSNAME : undefined;
    var isLinkPickerEnabled = !!lpLinkPicker;
    return _objectSpread(_objectSpread({
      title: intl.formatMessage(messages.card),
      className: className,
      nodeType: nodeType,
      preventPopupOverflow: isLinkPickerEnabled
    }, toolbarOffset), {}, {
      getDomRef: function getDomRef(view) {
        var element = findDomRefAtPos(view.state.selection.from, view.domAtPos.bind(view));
        if (!element) {
          return undefined;
        }
        if (isEmbedCard) {
          return element.querySelector(".".concat(richMediaClassName));
        }
        return element;
      },
      items: generateToolbarItems(state, intl, providerFactory, cardOptions, lpLinkPicker, platform, linkPickerOptions, pluginInjectionApi),
      scrollable: pluginState !== null && pluginState !== void 0 && pluginState.showLinkingToolbar ? false : true
    }, editLinkToolbarConfig(Boolean(pluginState === null || pluginState === void 0 ? void 0 : pluginState.showLinkingToolbar), isLinkPickerEnabled));
  };
};
var unlinkCard = function unlinkCard(node, state, editorAnalyticsApi) {
  var displayInfo = displayInfoForCard(node, findCardInfo(state));
  var text = displayInfo.title || displayInfo.url;
  if (text) {
    return commandWithMetadata(changeSelectedCardToText(text, editorAnalyticsApi), {
      action: ACTION.UNLINK
    });
  }
  return function () {
    return false;
  };
};
var buildAlignmentOptions = function buildAlignmentOptions(state, intl, widthPluginDependencyApi, analyticsApi, cardOptions) {
  return buildLayoutButtons(state, intl, state.schema.nodes.embedCard, widthPluginDependencyApi, analyticsApi, true, true, cardOptions === null || cardOptions === void 0 ? void 0 : cardOptions.allowWrapping, cardOptions === null || cardOptions === void 0 ? void 0 : cardOptions.allowAlignment);
};
var withToolbarMetadata = function withToolbarMetadata(command) {
  return commandWithMetadata(command, {
    inputMethod: INPUT_METHOD.FLOATING_TB
  });
};
var getToolbarViewedItem = function getToolbarViewedItem(url, display) {
  if (!url) {
    return [];
  }
  return [{
    type: 'custom',
    fallback: [],
    render: function render(editorView) {
      return /*#__PURE__*/React.createElement(ToolbarViewedEvent, {
        key: "edit.link.menu.viewed",
        url: url,
        display: display,
        editorView: editorView
      });
    }
  }];
};
var generateToolbarItems = function generateToolbarItems(state, intl, providerFactory, cardOptions, lpLinkPicker, platform, linkPicker, pluginInjectionApi) {
  return function (node) {
    var _pluginInjectionApi$a, _pluginInjectionApi$d, _pluginInjectionApi$d2, _node$attrs;
    var _titleUrlPairFromNode2 = titleUrlPairFromNode(node),
      url = _titleUrlPairFromNode2.url;
    var _ref = (_pluginInjectionApi$a = pluginInjectionApi === null || pluginInjectionApi === void 0 ? void 0 : pluginInjectionApi.analytics) !== null && _pluginInjectionApi$a !== void 0 ? _pluginInjectionApi$a : {},
      editorAnalyticsApi = _ref.actions;
    var metadata = {};
    if (url && !isSafeUrl(url)) {
      return [];
    } else {
      var _displayInfoForCard = displayInfoForCard(node, findCardInfo(state)),
        title = _displayInfoForCard.title;
      metadata = {
        url: url,
        title: title
      };
    }
    var pluginState = pluginKey.getState(state);
    var currentAppearance = appearanceForNodeType(node.type);
    var _ref2 = (_pluginInjectionApi$d = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$d2 = pluginInjectionApi.decorations) === null || _pluginInjectionApi$d2 === void 0 ? void 0 : _pluginInjectionApi$d2.actions) !== null && _pluginInjectionApi$d !== void 0 ? _pluginInjectionApi$d : {},
      hoverDecoration = _ref2.hoverDecoration;
    var isDatasource = isDatasourceNode(node);
    var shouldRenderDatasourceToolbar = isDatasource &&
    // not showing toolbar in mobile for now since not sure what our plans are for it
    platform !== 'mobile' && cardOptions.allowDatasource && canRenderDatasource(node === null || node === void 0 || (_node$attrs = node.attrs) === null || _node$attrs === void 0 || (_node$attrs = _node$attrs.datasource) === null || _node$attrs === void 0 ? void 0 : _node$attrs.id);

    /* mobile builds toolbar natively using toolbarItems */
    if (pluginState !== null && pluginState !== void 0 && pluginState.showLinkingToolbar && platform !== 'mobile') {
      return [buildEditLinkToolbar({
        providerFactory: providerFactory,
        linkPicker: linkPicker,
        node: node,
        pluginInjectionApi: pluginInjectionApi,
        lpLinkPicker: lpLinkPicker
      })];
    } else if (shouldRenderDatasourceToolbar) {
      return getDatasourceButtonGroup(metadata, intl, editorAnalyticsApi, node, hoverDecoration, node.attrs.datasource.id, state, cardOptions, currentAppearance, platform);
    } else {
      var inlineCard = state.schema.nodes.inlineCard;
      var isEditDropdownEnabled = platform !== 'mobile' && cardOptions.allowDatasource && fg('platform.linking-platform.enable-datasource-edit-dropdown-toolbar');
      var editItems = isEditDropdownEnabled ? [{
        type: 'custom',
        fallback: [],
        render: function render(editorView) {
          return /*#__PURE__*/React.createElement(EditToolbarButton, {
            key: "edit-toolbar-item",
            url: url,
            intl: intl,
            editorAnalyticsApi: editorAnalyticsApi,
            editorView: editorView,
            onLinkEditClick: editLink(editorAnalyticsApi, true),
            currentAppearance: currentAppearance
          });
        }
      }] : [{
        id: 'editor.link.edit',
        type: 'button',
        selected: false,
        metadata: metadata,
        title: intl.formatMessage(linkToolbarMessages.editLink),
        showTitle: true,
        testId: 'link-toolbar-edit-link-button',
        onClick: editLink(editorAnalyticsApi, true)
      }, {
        type: 'separator'
      }];
      var toolbarItems = [].concat(editItems, [{
        id: 'editor.link.openLink',
        type: 'button',
        icon: OpenIcon,
        metadata: metadata,
        className: 'hyperlink-open-link',
        title: intl.formatMessage(linkMessages.openLink),
        onClick: visitCardLink(editorAnalyticsApi)
      }, {
        type: 'separator'
      }], _toConsumableArray(getUnlinkButtonGroup(state, intl, node, inlineCard, editorAnalyticsApi)), [{
        type: 'copy-button',
        supportsViewMode: true,
        items: [{
          state: state,
          formatMessage: intl.formatMessage,
          nodeType: node.type
        }]
      }, {
        type: 'separator'
      }], _toConsumableArray(getSettingsButtonGroup(intl, editorAnalyticsApi, cardOptions.userPreferencesLink)), [{
        id: 'editor.link.delete',
        focusEditoronEnter: true,
        type: 'button',
        appearance: 'danger',
        icon: RemoveIcon,
        onMouseEnter: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, true),
        onMouseLeave: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, false),
        onFocus: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, true),
        onBlur: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, false),
        title: intl.formatMessage(commonMessages.remove),
        onClick: withToolbarMetadata(removeCard(editorAnalyticsApi))
      }]);
      if (currentAppearance === 'embed') {
        var _pluginInjectionApi$a2;
        var alignmentOptions = buildAlignmentOptions(state, intl, pluginInjectionApi === null || pluginInjectionApi === void 0 ? void 0 : pluginInjectionApi.width, pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a2 = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a2 === void 0 ? void 0 : _pluginInjectionApi$a2.actions, cardOptions);
        if (alignmentOptions.length) {
          alignmentOptions.push({
            type: 'separator'
          });
        }
        toolbarItems.unshift.apply(toolbarItems, _toConsumableArray(alignmentOptions));
      }
      var allowBlockCards = cardOptions.allowBlockCards,
        allowEmbeds = cardOptions.allowEmbeds,
        allowDatasource = cardOptions.allowDatasource,
        showUpgradeDiscoverability = cardOptions.showUpgradeDiscoverability;
      // This code will be executed only for appearances such as "inline", "block" & "embed"
      // For url appearance, please see HyperlinkToolbarAppearanceProps
      if (currentAppearance) {
        var showDatasourceAppearance = allowDatasource && url && fg('platform.linking-platform.enable-datasource-appearance-toolbar');
        toolbarItems.unshift.apply(toolbarItems, _toConsumableArray(getToolbarViewedItem(url, currentAppearance)).concat([{
          type: 'custom',
          fallback: [],
          render: function render(editorView) {
            return /*#__PURE__*/React.createElement(LinkToolbarAppearance, {
              key: "link-appearance",
              url: url,
              intl: intl,
              currentAppearance: currentAppearance,
              editorView: editorView,
              editorState: state,
              allowEmbeds: allowEmbeds,
              allowBlockCards: allowBlockCards,
              platform: platform,
              editorAnalyticsApi: editorAnalyticsApi,
              showUpgradeDiscoverability: showUpgradeDiscoverability
            });
          }
        }], _toConsumableArray(showDatasourceAppearance ? [{
          type: 'custom',
          fallback: [],
          render: function render(editorView) {
            return /*#__PURE__*/React.createElement(DatasourceAppearanceButton, {
              intl: intl,
              editorAnalyticsApi: editorAnalyticsApi,
              url: url,
              editorView: editorView,
              editorState: state,
              inputMethod: INPUT_METHOD.FLOATING_TB
            });
          }
        }] : []), [{
          type: 'separator'
        }]));
      }
      var shouldShowDatasourceEditButton = platform !== 'mobile' && allowDatasource && !fg('platform.linking-platform.enable-datasource-edit-dropdown-toolbar');
      if (shouldShowDatasourceEditButton) {
        toolbarItems.unshift({
          type: 'custom',
          fallback: [],
          render: function render(editorView) {
            return /*#__PURE__*/React.createElement(EditDatasourceButton, {
              intl: intl,
              editorAnalyticsApi: editorAnalyticsApi,
              url: url,
              editorView: editorView,
              currentAppearance: currentAppearance
            });
          }
        });
      }
      return toolbarItems;
    }
  };
};
var getUnlinkButtonGroup = function getUnlinkButtonGroup(state, intl, node, inlineCard, editorAnalyticsApi) {
  return node.type === inlineCard ? [{
    id: 'editor.link.unlink',
    focusEditoronEnter: true,
    type: 'button',
    title: intl.formatMessage(linkToolbarMessages.unlink),
    icon: UnlinkIcon,
    onClick: withToolbarMetadata(unlinkCard(node, state, editorAnalyticsApi))
  }, {
    type: 'separator'
  }] : [];
};
export var getHyperlinkToolbarSettingsButton = function getHyperlinkToolbarSettingsButton(intl, editorAnalyticsApi, userPreferencesLink) {
  return {
    id: 'editor.link.settings',
    type: 'button',
    icon: CogIcon,
    title: intl.formatMessage(linkToolbarMessages.settingsLink),
    onClick: fg('platform.editor.card.inject-settings-button') ? openLinkSettings(editorAnalyticsApi, userPreferencesLink) : openLinkSettings(editorAnalyticsApi, undefined),
    href: fg('platform.editor.card.inject-settings-button') ? userPreferencesLink || getLinkPreferencesURLFromENV() : getLinkPreferencesURLFromENV(),
    target: '_blank'
  };
};
export var getSettingsButton = function getSettingsButton(intl, editorAnalyticsApi, userPreferencesLink) {
  return {
    id: 'editor.link.settings',
    type: 'button',
    icon: CogIcon,
    title: intl.formatMessage(linkToolbarMessages.settingsLink),
    onClick: fg('platform.editor.card.inject-settings-button') ? openLinkSettings(editorAnalyticsApi, userPreferencesLink) : openLinkSettings(editorAnalyticsApi, undefined)
  };
};
export var getSettingsButtonGroup = function getSettingsButtonGroup(intl, editorAnalyticsApi, userPreferencesLink) {
  return [getSettingsButton(intl, editorAnalyticsApi, userPreferencesLink), {
    type: 'separator'
  }];
};
var getDatasourceButtonGroup = function getDatasourceButtonGroup(metadata, intl, editorAnalyticsApi, node, hoverDecoration, datasourceId, state, cardOptions, currentAppearance, platform) {
  var _node$attrs2;
  var toolbarItems = [];
  if (isDatasourceConfigEditable(datasourceId) && !fg('platform.linking-platform.enable-datasource-edit-dropdown-toolbar')) {
    toolbarItems.push({
      id: 'editor.edit.datasource',
      type: 'button',
      icon: SmallerEditIcon,
      metadata: metadata,
      className: 'datasource-edit',
      title: intl.formatMessage(linkToolbarMessages.editDatasource),
      onClick: editDatasource(datasourceId, editorAnalyticsApi, 'datasource'),
      testId: 'datasource-edit-button'
    }, {
      type: 'separator'
    });
  }
  var canShowAppearanceSwitch = function canShowAppearanceSwitch() {
    // we do not show smart-link or the datasource icons when the node does not have a url to resolve
    if (!metadata.url) {
      return false;
    }
    if (
    // FF that controls visibily of the additional toolbar buttons
    !fg('platform.linking-platform.enable-datasource-appearance-toolbar')) {
      return false;
    }

    // FF to enable additional toolbar buttons based on if the datasource is configurable or not
    return fg('platform.linking-platform.datasource-enable-toolbar-buttons-for-all-datasources') ? true : !isDatasourceConfigEditable(datasourceId);
  };
  if (canShowAppearanceSwitch()) {
    var allowBlockCards = cardOptions.allowBlockCards,
      allowEmbeds = cardOptions.allowEmbeds,
      showUpgradeDiscoverability = cardOptions.showUpgradeDiscoverability;
    var url = metadata.url;
    toolbarItems.push({
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        return /*#__PURE__*/React.createElement(LinkToolbarAppearance, {
          key: "link-appearance",
          url: url,
          intl: intl,
          currentAppearance: currentAppearance,
          editorView: editorView,
          editorState: state,
          allowEmbeds: allowEmbeds,
          allowBlockCards: allowBlockCards,
          platform: platform,
          editorAnalyticsApi: editorAnalyticsApi,
          showUpgradeDiscoverability: showUpgradeDiscoverability,
          isDatasourceView: true
        });
      }
    }, {
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        return /*#__PURE__*/React.createElement(DatasourceAppearanceButton, {
          intl: intl,
          editorAnalyticsApi: editorAnalyticsApi,
          url: url,
          editorView: editorView,
          editorState: state,
          selected: true,
          inputMethod: INPUT_METHOD.FLOATING_TB
        });
      }
    }, {
      type: 'separator'
    });
  }
  if (fg('platform.linking-platform.enable-datasource-edit-dropdown-toolbar')) {
    toolbarItems.push({
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        return /*#__PURE__*/React.createElement(EditToolbarButton, {
          datasourceId: datasourceId,
          node: node,
          key: "edit-toolbar-item",
          url: metadata.url,
          intl: intl,
          editorAnalyticsApi: editorAnalyticsApi,
          editorView: editorView,
          onLinkEditClick: editLink(editorAnalyticsApi, false),
          currentAppearance: "datasource"
        });
      }
    });
  }
  if (node !== null && node !== void 0 && (_node$attrs2 = node.attrs) !== null && _node$attrs2 !== void 0 && _node$attrs2.url) {
    toolbarItems.push({
      id: 'editor.link.openLink',
      type: 'button',
      icon: OpenIcon,
      metadata: metadata,
      className: 'hyperlink-open-link',
      title: intl.formatMessage(linkMessages.openLink),
      onClick: visitCardLink(editorAnalyticsApi)
    }, {
      type: 'separator'
    });
  }
  toolbarItems.push.apply(toolbarItems, [{
    type: 'copy-button',
    supportsViewMode: true,
    items: [{
      state: state,
      formatMessage: intl.formatMessage,
      nodeType: node.type
    }]
  }, {
    type: 'separator'
  }].concat(_toConsumableArray(getSettingsButtonGroup(intl, editorAnalyticsApi, cardOptions === null || cardOptions === void 0 ? void 0 : cardOptions.userPreferencesLink)), [{
    id: 'editor.link.delete',
    focusEditoronEnter: true,
    type: 'button',
    appearance: 'danger',
    icon: RemoveIcon,
    onMouseEnter: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, true),
    onMouseLeave: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, false),
    onFocus: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, true),
    onBlur: hoverDecoration === null || hoverDecoration === void 0 ? void 0 : hoverDecoration(node.type, false),
    title: intl.formatMessage(commonMessages.remove),
    onClick: withToolbarMetadata(removeCard(editorAnalyticsApi))
  }]));
  return toolbarItems;
};
export var shouldRenderToolbarPulse = function shouldRenderToolbarPulse(embedEnabled, appearance, status, isDiscoverabilityEnabled) {
  return embedEnabled && appearance === 'inline' && status === 'resolved' && isDiscoverabilityEnabled;
};
export var getStartingToolbarItems = function getStartingToolbarItems(options, api) {
  return function (intl, link, providerFactory, onEditLink, metadata) {
    var isEditDropdownEnabled = options.platform !== 'mobile' && options.allowDatasource && fg('platform.linking-platform.enable-datasource-edit-dropdown-toolbar');
    var editLinkItem = isEditDropdownEnabled ? [{
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        var _api$analytics;
        if (!editorView) {
          return null;
        }
        return /*#__PURE__*/React.createElement(EditToolbarButton, {
          key: "edit-toolbar-button",
          intl: intl,
          editorAnalyticsApi: api === null || api === void 0 || (_api$analytics = api.analytics) === null || _api$analytics === void 0 ? void 0 : _api$analytics.actions,
          url: link,
          editorView: editorView,
          onLinkEditClick: onEditLink,
          currentAppearance: "url"
        });
      }
    }] : [{
      id: 'editor.link.edit',
      testId: 'editor.link.edit',
      type: 'button',
      onClick: onEditLink,
      title: intl.formatMessage(linkToolbarMessages.editLink),
      showTitle: true,
      metadata: metadata
    }, {
      type: 'separator'
    }];
    return [{
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        return /*#__PURE__*/React.createElement(ToolbarViewedEvent, {
          key: "edit.link.menu.viewed",
          url: link,
          display: "url",
          editorView: editorView
        });
      }
    }, {
      type: 'custom',
      fallback: [],
      render: function render(editorView) {
        var _api$analytics2;
        if (!editorView) {
          return null;
        }
        return /*#__PURE__*/React.createElement(HyperlinkToolbarAppearance, {
          key: "link-appearance",
          url: link,
          intl: intl,
          editorView: editorView,
          editorState: editorView.state,
          cardOptions: options,
          providerFactory: providerFactory,
          platform: options === null || options === void 0 ? void 0 : options.platform,
          editorAnalyticsApi: api === null || api === void 0 || (_api$analytics2 = api.analytics) === null || _api$analytics2 === void 0 ? void 0 : _api$analytics2.actions,
          editorPluginApi: api
        });
      }
    }].concat(editLinkItem);
  };
};
export var getEndingToolbarItems = function getEndingToolbarItems(options, api) {
  return function (intl, link) {
    if (fg('platform.editor.card.inject-settings-button')) {
      /**
       * Require either provider to be supplied (controls link preferences)
       * Or explicit user preferences config in order to enable button
       */
      if (options.provider || options.userPreferencesLink) {
        var _api$analytics3;
        return [{
          type: 'separator'
        }, getHyperlinkToolbarSettingsButton(intl, api === null || api === void 0 || (_api$analytics3 = api.analytics) === null || _api$analytics3 === void 0 ? void 0 : _api$analytics3.actions, options.userPreferencesLink)];
      }
    }
    return [];
  };
};